import { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { PlanogramItem, UnrangedPlanogramItem } from "@CommonTypes/merchflow/pog/item";
import { useAtomValue } from "jotai";
import React from "react";
import styled from "styled-components";

import { InnerWrapper, PLANOGRAM_LAYER_Z_INDEX } from "../../common/InnerWrapper";
import { PogAtom, Scaleable } from "../../store/types";
import { MergedShelf } from "./MergedShelf";
import { ResponseMerchFlowApplyFilters } from "@CommonApi/merchandise";
import { Hangcell } from "./Hangcell";
import { DEFAULT_NOTCH_RULER_WIDTH } from "../Ruler/PdfNotchRulers";

interface Props extends Scaleable {
  currentStoreCode?: string;
  pogAtom: PogAtom;
  isEditable: boolean;
  isPog: boolean;
  highlightedItems: string[];
  setIsModified: (modified: boolean) => void;
  availableStores: ResponseMerchFlowApplyFilters["filter_config"] | null;
  pdfMode?: boolean;
  customItemLabelOnly?: boolean;
  customLabelRule?: (item: PlanogramItem | UnrangedPlanogramItem) => string | null | undefined;
  pdfShelfLabelBayNo?: number;
  disableMergedShelves?: boolean;
  pdfNotchRulerWidth?: number;
}

interface ShelfForRenderGrouping<TShelf> {
  mergedLeft: TShelf | null;
  getMergedShelves: () => TShelf[];
}

const ShelfFixturesLayer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
`;

export const getShelfRenderGroups = <TShelf extends ShelfForRenderGrouping<TShelf>>(
  shelves: TShelf[],
  disableMergedShelves = false,
) => {
  return shelves.flatMap((shelf) => {
    if (!disableMergedShelves && shelf.mergedLeft !== null) {
      return [];
    }

    const shelvesToRender = disableMergedShelves ? [shelf] : shelf.getMergedShelves();

    return [{ shelf, shelvesToRender }];
  });
};

export const Shelves = ({
  currentStoreCode,
  scaleX,
  scaleY,
  pogAtom,
  isEditable,
  isPog,
  highlightedItems,
  setIsModified,
  availableStores,
  pdfMode = false,
  customItemLabelOnly,
  customLabelRule,
  pdfShelfLabelBayNo,
  disableMergedShelves = false,
  pdfNotchRulerWidth,
}: Props) => {
  const pog = useAtomValue(pogAtom);
  const bayShelfRenderGroups = pog.planogram.bays.map((bay, bayIndex) => {
    const rulerWidth = pdfNotchRulerWidth ?? DEFAULT_NOTCH_RULER_WIDTH;
    const bayShift = pdfMode ? (2 * bayIndex + 1) * rulerWidth : 0;

    return {
      bay,
      bayShift,
      renderGroups: getShelfRenderGroups(bay.shelves, disableMergedShelves),
    };
  });

  const getBounds = (shelves: PlanogramShelf[]) => {
    const firstShelf = shelves[0];

    return {
      xLeft: firstShelf.getXLeft(),
      xRight: firstShelf.getXRight(),
      yBottom: firstShelf.bay.notchOffset,
      yTop: firstShelf.bay.height,
    };
  };

  return (
    <InnerWrapper className="shelves" $zIndex={PLANOGRAM_LAYER_Z_INDEX.shelves}>
      <ShelfFixturesLayer className="shelf-fixtures">
        {bayShelfRenderGroups.map(({ bay, bayShift, renderGroups }, i) => (
          <React.Fragment key={i}>
            {renderGroups.map(({ shelf, shelvesToRender }, i) => {
              if (shelf.type === "HANGCELL") {
                return (
                  <Hangcell
                    key={i}
                    currentStoreCode={currentStoreCode}
                    bay={bay}
                    shelves={shelvesToRender}
                    bounds={getBounds(shelvesToRender)}
                    scaleX={scaleX}
                    scaleY={scaleY}
                    isEditable={isEditable}
                    isPog={isPog}
                    highlightedItems={highlightedItems}
                    pogAtom={pogAtom}
                    setIsModified={setIsModified}
                    availableStores={availableStores}
                    pdfMode={pdfMode}
                    customItemLabelOnly={customItemLabelOnly}
                    customLabelRule={customLabelRule}
                    pdfShelfLabelBayNo={pdfShelfLabelBayNo ?? (pdfMode ? bay.getNo() : undefined)}
                    bayShift={bayShift}
                  />
                );
              } else {
                return (
                  <MergedShelf
                    key={i}
                    currentStoreCode={currentStoreCode}
                    bay={bay}
                    shelves={shelvesToRender}
                    bounds={getBounds(shelvesToRender)}
                    scaleX={scaleX}
                    scaleY={scaleY}
                    isEditable={isEditable}
                    isPog={isPog}
                    highlightedItems={highlightedItems}
                    pogAtom={pogAtom}
                    setIsModified={setIsModified}
                    availableStores={availableStores}
                    pdfMode={pdfMode}
                    customItemLabelOnly={customItemLabelOnly}
                    customLabelRule={customLabelRule}
                    pdfShelfLabelBayNo={pdfShelfLabelBayNo ?? (pdfMode ? bay.getNo() : undefined)}
                    bayShift={bayShift}
                  />
                );
              }
            })}
          </React.Fragment>
        ))}
      </ShelfFixturesLayer>
    </InnerWrapper>
  );
};
